import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";

export default function FrameworkContact() {
    const [f, setF] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/contact", { brand: "framework", ...f });
            toast.success("Message received. We will reply as soon as possible.");
            setF({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
        } catch {
            toast.error("Failed to send. Please try again or email desk@frameworktennis.com.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="framework-contact-page">
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-16 grid lg:grid-cols-2 gap-12">
                <div>
                    <div className="fw-label mb-4">Contact</div>

                    <h1 className="fw-display text-5xl sm:text-7xl">
                        Start the <span className="text-[#a6ce39]">conversation.</span>
                    </h1>

                    <p className="mt-6 text-white/60 text-lg max-w-md">
                        Interested in coaching, stringing, Swing AI, mental performance,
                        or a custom training plan? Send us a message and we’ll help you
                        find the right place to start.
                    </p>

                    <div className="mt-10 space-y-4">
                        <div className="flex items-center gap-4 text-white/80">
                            <div className="w-10 h-10 border border-[#a6ce39] flex items-center justify-center">
                                <Mail size={16} className="text-[#a6ce39]" />
                            </div>
                            <div>
                                <div className="fw-label">Email</div>
                                <a
                                    href="mailto:desk@frameworktennis.com"
                                    className="fw-mono text-sm text-white/80 hover:text-[#a6ce39] transition-colors"
                                >
                                    desk@frameworktennis.com
                                </a>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 text-white/80">
                            <div className="w-10 h-10 border border-[#a6ce39] flex items-center justify-center">
                                <MapPin size={16} className="text-[#a6ce39]" />
                            </div>
                            <div>
                                <div className="fw-label">Base</div>
                                <div className="fw-mono text-sm">
                                    Metro Atlanta, GA
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={submit} className="border border-white/10 p-8 bg-[#0f0f10]">
                    <div className="space-y-6">
                        <FWL label="Name" required testid="fw-contact-name" value={f.name} onChange={(v) => setF({ ...f, name: v })} />
                        <FWL label="Email" required type="email" testid="fw-contact-email" value={f.email} onChange={(v) => setF({ ...f, email: v })} />
                        <FWL label="Phone" testid="fw-contact-phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />

                        <label className="block">
                            <div className="fw-label mb-2">Subject</div>
                            <select
                                value={f.subject}
                                onChange={(e) => setF({ ...f, subject: e.target.value })}
                                data-testid="fw-contact-subject"
                                className="fw-select"
                            >
                                {[
                                    "General Inquiry",
                                    "Physical Performance",
                                    "Mental Performance",
                                    "Technical Development",
                                    "Gear Optimization",
                                    "The Lab / Stringing",
                                    "Swing AI",
                                    "Events",
                                    "Media / Partnership",
                                ].map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            <div className="fw-label mb-2">Message *</div>
                            <textarea
                                rows={5}
                                required
                                value={f.message}
                                onChange={(e) => setF({ ...f, message: e.target.value })}
                                data-testid="fw-contact-message"
                                className="w-full bg-transparent border border-white/20 focus:border-[#a6ce39] outline-none text-white p-3 fw-mono text-sm"
                            />
                        </label>

                        <button type="submit" disabled={busy} className="fw-btn-primary" data-testid="fw-contact-submit">
                            {busy ? "Sending…" : "Send Message"} <Send size={16} />
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}

function FWL({ label, testid, value, onChange, type = "text", required }) {
    return (
        <label className="block">
            <div className="fw-label mb-2">
                {label}{required && <span className="text-[#a6ce39]"> *</span>}
            </div>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="fw-input"
            />
        </label>
    );
}